import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router';

import { cn } from '../../utils/style-utils';

type As = 'button' | 'a' | 'Link';
type BaseProps = {
  variant?:
    | 'primary'
    | 'secondary'
    | 'light'
    | 'outline'
    | 'ghost'
    | 'link'
    | 'success';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
};
type PolymorphicButtonProps<T> = BaseProps &
  (T extends 'button'
    ? { as?: T } & ButtonHTMLAttributes<HTMLButtonElement>
    : T extends 'Link'
      ? { as: T } & LinkProps
      : { as: T } & AnchorHTMLAttributes<HTMLAnchorElement>);

const UiButton = <T extends As>({
  as = 'button',
  variant = 'primary',
  size = 'md',
  fullWidth,
  className,
  children,
  leftIcon,
  rightIcon,
  ...rest
}: PolymorphicButtonProps<T>) => {
  const combinedClassName = cn([
    variant === 'primary' && 'btn-primary',
    variant === 'secondary' && 'btn-secondary',
    variant === 'light' && 'btn-light',
    variant === 'outline' && 'btn-outline',
    variant === 'ghost' && 'btn-ghost',
    variant === 'link' && 'btn-link',
    variant === 'success' && 'btn-success',
    size === 'xs' && 'text-sm md:text-base py-[1px] px-3',
    size === 'sm' && 'text-sm md:text-base py-0.5 px-4',
    size === 'md' && 'py-1 px-8',
    size === 'lg' && 'py-2 px-12',
    fullWidth && 'w-full',
    'text-center',
    className,
    (leftIcon || rightIcon) &&
      'inline-flex gap-x-1.5 justify-center items-center',
  ]);

  const content =
    leftIcon || rightIcon ? (
      <>
        {leftIcon && <span className="*:w-4">{leftIcon}</span>}
        <span>{children}</span>
        {rightIcon && <span className="*:w-4">{rightIcon}</span>}
      </>
    ) : (
      children
    );
  if (as === 'a') {
    return (
      <a
        className={combinedClassName}
        {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
        tabIndex={0}
      >
        {content}
      </a>
    );
  } else if (as === 'Link') {
    return (
      <Link className={combinedClassName} {...(rest as unknown as LinkProps)}>
        {content}
      </Link>
    );
  } else {
    return (
      <button
        className={combinedClassName}
        {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {content}
      </button>
    );
  }
};

export default UiButton;
